import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/AccountsAPI/verifyShareCapitalAPI';

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || fallback;

export const fetchShareCapitalQueue = createAsyncThunk(
    'verifyShareCapital/fetchQueue',
    async (roleId, { rejectWithValue }) => {
        try { return await api.getVerifyShareCapitalGrid(roleId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch share capital verification queue')); }
    }
);

export const fetchShareCapitalDetail = createAsyncThunk(
    'verifyShareCapital/fetchDetail',
    async (refNo, { rejectWithValue }) => {
        try { return await api.getVerifyShareCapitalView(refNo); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch share capital details')); }
    }
);

export const fetchCapitalDetails = createAsyncThunk(
    'verifyShareCapital/fetchCapitalDetails',
    async (_, { rejectWithValue }) => {
        try { return await api.getCapitalDetails(); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch existing capital')); }
    }
);

export const submitApproveShareCapital = createAsyncThunk(
    'verifyShareCapital/approve',
    async (payload, { rejectWithValue }) => {
        try { return await api.approveShareCapital(payload); }
        catch (e) { return rejectWithValue(err(e, 'Error Occurred While Verification')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    detail: null, detailLoading: false, detailError: null,
    capital: [], capitalLoading: false,
    verifyLoading: false,
};

const verifyShareCapitalSlice = createSlice({
    name: 'verifyShareCapital',
    initialState,
    reducers: {
        clearShareCapitalDetail: (s) => { s.detail = null; s.detailError = null; s.detailLoading = false; },
        resetVerifyShareCapital: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchShareCapitalQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchShareCapitalQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchShareCapitalQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        builder
            .addCase(fetchShareCapitalDetail.pending, (s) => { s.detailLoading = true; s.detailError = null; s.detail = null; })
            .addCase(fetchShareCapitalDetail.fulfilled, (s, a) => { s.detailLoading = false; s.detail = a.payload?.Data || null; })
            .addCase(fetchShareCapitalDetail.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = null; });

        builder
            .addCase(fetchCapitalDetails.pending, (s) => { s.capitalLoading = true; })
            .addCase(fetchCapitalDetails.fulfilled, (s, a) => { s.capitalLoading = false; s.capital = a.payload?.Data || []; })
            .addCase(fetchCapitalDetails.rejected, (s) => { s.capitalLoading = false; s.capital = []; });

        builder
            .addCase(submitApproveShareCapital.pending, (s) => { s.verifyLoading = true; })
            .addCase(submitApproveShareCapital.fulfilled, (s) => { s.verifyLoading = false; })
            .addCase(submitApproveShareCapital.rejected, (s) => { s.verifyLoading = false; });
    },
});

export const { clearShareCapitalDetail, resetVerifyShareCapital } = verifyShareCapitalSlice.actions;

export const selectVSCQueue = (s) => s.verifyShareCapital.queue;
export const selectVSCQueueLoading = (s) => s.verifyShareCapital.queueLoading;
export const selectVSCQueueError = (s) => s.verifyShareCapital.queueError;
export const selectVSCDetail = (s) => s.verifyShareCapital.detail;
export const selectVSCDetailLoading = (s) => s.verifyShareCapital.detailLoading;
export const selectVSCDetailError = (s) => s.verifyShareCapital.detailError;
export const selectVSCCapital = (s) => s.verifyShareCapital.capital;
export const selectVSCCapitalLoading = (s) => s.verifyShareCapital.capitalLoading;
export const selectVSCVerifyLoading = (s) => s.verifyShareCapital.verifyLoading;

export default verifyShareCapitalSlice.reducer;
